import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import NetworkService from '../services/NetworkService';
import { executeWithRetry } from '../config/supabase';

/**
 * Custom hook for connection-aware form submissions
 * Handles network checking, retries, and user feedback
 */
export const useConnectionAwareSubmit = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForConnection, setIsWaitingForConnection] = useState(false);

  const submitWithConnectionCheck = useCallback(async (
    submitFunction,
    options = {}
  ) => {
    const {
      loadingMessage = 'Submitting...',
      offlineMessage = 'No internet connection. Waiting for connection...',
      retryMessage = 'Connection restored! Retrying...',
      successMessage = 'Success!',
      errorMessage = 'Failed to submit. Please try again.',
      maxRetries = 3,
      retryDelay = 2000
    } = options;

    // Check if already submitting
    if (isSubmitting || isWaitingForConnection) {
      return { success: false, error: 'Already submitting' };
    }

    // Initial connection check
    if (!NetworkService.isOnline) {
      setIsWaitingForConnection(true);
      
      const waitToast = toast.loading(offlineMessage, {
        duration: Infinity,
        style: { 
          background: '#f59e0b', 
          color: 'white',
          maxWidth: '400px'
        }
      });

      // Wait for connection to be restored
      return new Promise((resolve) => {
        const connectionListener = async (isOnline) => {
          if (isOnline) {
            NetworkService.removeListener(connectionListener);
            toast.dismiss(waitToast);
            toast.success(retryMessage);
            
            setIsWaitingForConnection(false);
            
            // Retry the submission after connection is restored
            const result = await submitWithConnectionCheck(submitFunction, options);
            resolve(result);
          }
        };

        NetworkService.addListener(connectionListener);
      });
    }

    setIsSubmitting(true);
    
    const loadingToast = toast.loading(loadingMessage);

    try {
      // Execute with retry logic
      const result = await executeWithRetry(async () => {
        // Double-check connection before each attempt
        if (!NetworkService.isOnline) {
          throw new Error('Connection lost during submission');
        }
        
        return await submitFunction();
      }, maxRetries, retryDelay);

      toast.dismiss(loadingToast);
      
      if (result.success !== false) {
        toast.success(successMessage);
        return { success: true, data: result };
      } else {
        toast.error(result.error || errorMessage);
        return { success: false, error: result.error || errorMessage };
      }

    } catch (error) {
      toast.dismiss(loadingToast);
      
      // Check if it's a network error
      if (NetworkService.isNetworkError(error) || error.message.includes('Connection lost')) {
        // Network error - offer to wait for connection
        setIsWaitingForConnection(true);
        
        const retryToast = toast.error(
          'Connection lost. Tap here when connection is restored to retry.',
          {
            duration: Infinity,
            onClick: async () => {
              toast.dismiss(retryToast);
              setIsWaitingForConnection(false);
              const result = await submitWithConnectionCheck(submitFunction, options);
              return result;
            },
            style: { 
              background: '#ef4444', 
              color: 'white',
              cursor: 'pointer',
              maxWidth: '400px'
            }
          }
        );

        return { success: false, error: 'Connection lost', retryable: true };
      } else {
        // Non-network error
        console.error('Submit error:', error);
        toast.error(error.message || errorMessage);
        return { success: false, error: error.message || errorMessage };
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, isWaitingForConnection]);

  return {
    submitWithConnectionCheck,
    isSubmitting,
    isWaitingForConnection
  };
}; 